import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import { retry } from '@reduxjs/toolkit/query'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'

import { HTTP_METHODS, HTTP_RESPONSE_STATUS } from 'common/constants/httpMethodsConstants'
import { LOCALE_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import type { IUser } from 'common/interfaces/IUser'
import { env } from 'common/utils/env'
import { stringifyResponseForError } from 'common/utils/text.utils'
import { PATH_SEGMENT } from 'routes/pathSegments'

type ApiRequestArgs = {
  url: string
  data?: AxiosRequestConfig['data']
  method?: AxiosRequestConfig['method']
  headers?: AxiosRequestConfig['headers']
  params?: AxiosRequestConfig['params']
  responseType?: AxiosRequestConfig['responseType']
  requireSuccessTrue?: boolean
}

type ApiBaseQueryErrorStatus = number | 'FETCH_ERROR' | 'TIMEOUT_ERROR' | 'CUSTOM_ERROR'

type SemanticFailureData = {
  code: 'SUCCESS_NOT_TRUE'
  message: string
  response: unknown
  retryable: true
}

type ApiBaseQueryError = {
  status: ApiBaseQueryErrorStatus
  data: unknown
}

const SUCCESS_NOT_TRUE_CODE = 'SUCCESS_NOT_TRUE'
const MAX_RETRIES = 5
const INITIAL_BACKOFF_MS = 2000
const MAX_BACKOFF_MS = 32000
const JITTER_MIN_MULTIPLIER = 0.8
const JITTER_MAX_MULTIPLIER = 1.2

const toRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  return value as Record<string, unknown>
}

const getStoredUser = (): IUser | null => {
  const storageUser = window.localStorage.getItem(LOCALE_STORAGE_KEYS.USER)

  if (!storageUser) {
    return null
  }

  try {
    return JSON.parse(storageUser)
  } catch {
    return null
  }
}

const handleUnauthorizedResponse = (): void => {
  const user = getStoredUser()

  if (user?.old_access_token) {
    const newUser = {
      ...user,
      access_token: user.old_access_token,
      is_super_doctor: true,
    }
    delete newUser.old_access_token
    window.localStorage.setItem(LOCALE_STORAGE_KEYS.USER, JSON.stringify(newUser))
    window.location.assign(PATH_SEGMENT.ROOT)
    return
  }

  window.localStorage.clear()
  window.location.reload()
}

const extractErrorData = (responseData: unknown, fallbackMessage: string): unknown => {
  const responseRecord = toRecord(responseData)
  const errors = responseRecord?.error

  if (errors && typeof errors === 'object') {
    return Object.values(errors as Record<string, unknown>).flat()
  }

  if (typeof errors === 'string' && errors.length > 0) {
    return [errors]
  }

  if (typeof responseRecord?.message === 'string' && responseRecord.message.length > 0) {
    return [responseRecord.message]
  }

  return [fallbackMessage]
}

const isSuccessTrueResponse = (responseData: unknown): boolean => {
  const responseRecord = toRecord(responseData)
  return responseRecord?.success === true
}

const createSemanticSuccessError = (responseData: unknown): ApiBaseQueryError => {
  const responseRecord = toRecord(responseData)
  const responseAsJson = stringifyResponseForError(responseData)
  const baseMessage =
    typeof responseRecord?.message === 'string' && responseRecord.message.length > 0
      ? responseRecord.message
      : 'Expected response.success === true'

  const data: SemanticFailureData = {
    code: SUCCESS_NOT_TRUE_CODE,
    message: `${baseMessage} Response: ${responseAsJson}`,
    response: responseData,
    retryable: true,
  }

  return {
    status: 'CUSTOM_ERROR',
    data,
  }
}

const resolveErrorStatus = (error: unknown): ApiBaseQueryErrorStatus => {
  const errorRecord = toRecord(error)
  const code = errorRecord?.code

  if (code === 'ECONNABORTED') {
    return 'TIMEOUT_ERROR'
  }

  const responseRecord = toRecord(errorRecord?.response)
  const status = responseRecord?.status

  if (typeof status === 'number') {
    return status
  }

  return 'FETCH_ERROR'
}

const resolveErrorResponseData = (error: unknown): unknown => {
  const errorRecord = toRecord(error)
  const responseRecord = toRecord(errorRecord?.response)
  return responseRecord?.data
}

const isSemanticRetryableError = (error: ApiBaseQueryError): boolean => {
  if (error.status !== 'CUSTOM_ERROR') {
    return false
  }

  const errorData = toRecord(error.data)
  return errorData?.code === SUCCESS_NOT_TRUE_CODE && errorData?.retryable === true
}

const shouldRetryByStatus = (status: number): boolean => {
  return status === 408 || status === 429 || status >= 500
}

const shouldRetry = (error: ApiBaseQueryError): boolean => {
  if (isSemanticRetryableError(error)) {
    return true
  }

  if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR') {
    return true
  }

  return typeof error.status === 'number' ? shouldRetryByStatus(error.status) : false
}

const resolveMaxRetries = (extraOptions: unknown): number => {
  const extraOptionsRecord = toRecord(extraOptions)
  return typeof extraOptionsRecord?.maxRetries === 'number'
    ? extraOptionsRecord.maxRetries
    : MAX_RETRIES
}

const waitForBackoffDelay = (timeoutMs: number, signal?: AbortSignal): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      if (signal) {
        signal.removeEventListener('abort', abortHandler)
      }
      resolve()
    }, timeoutMs)

    const abortHandler = () => {
      clearTimeout(timeoutId)
      reject(new Error('Aborted'))
    }

    if (!signal) {
      return
    }

    if (signal.aborted) {
      abortHandler()
      return
    }

    signal.addEventListener('abort', abortHandler, { once: true })
  })

const retryBackoff = async (attempt: number = 1, _maxRetries?: number, signal?: AbortSignal) => {
  const normalizedAttempt = Math.max(attempt, 1)
  const exponentialDelay = Math.min(
    MAX_BACKOFF_MS,
    INITIAL_BACKOFF_MS * 2 ** (normalizedAttempt - 1),
  )
  const jitterMultiplier =
    JITTER_MIN_MULTIPLIER + Math.random() * (JITTER_MAX_MULTIPLIER - JITTER_MIN_MULTIPLIER)
  const timeoutMs = Math.floor(exponentialDelay * jitterMultiplier)

  await waitForBackoffDelay(timeoutMs, signal)
}

const rawBaseQuery: BaseQueryFn<ApiRequestArgs, unknown, ApiBaseQueryError> = async (param) => {
  const { requireSuccessTrue, ...requestConfig } = param

  try {
    const user = getStoredUser()
    const result: AxiosResponse = await axios({
      ...requestConfig,
      method: requestConfig.method ?? HTTP_METHODS.GET,
      baseURL: env.RESIDEN_API_BASE_URL,
      headers: {
        ...requestConfig.headers,
        Authorization: user ? `Bearer ${user.access_token}` : null,
      },
      responseType: requestConfig.responseType,
    })

    if (requireSuccessTrue && !isSuccessTrueResponse(result.data)) {
      return {
        error: createSemanticSuccessError(result.data),
      }
    }

    return { data: result.data }
  } catch (error) {
    const status = resolveErrorStatus(error)

    if (status === HTTP_RESPONSE_STATUS.UNAUTHORIZED) {
      handleUnauthorizedResponse()
    }

    const fallbackMessage =
      typeof toRecord(error)?.message === 'string'
        ? String(toRecord(error)?.message)
        : 'Request failed'

    return {
      error: {
        status,
        data: extractErrorData(resolveErrorResponseData(error), fallbackMessage),
      },
    }
  }
}

const baseQueryWithRetry = retry(rawBaseQuery, {
  backoff: retryBackoff,
  retryCondition: (error, _args, { attempt, extraOptions }) =>
    attempt <= resolveMaxRetries(extraOptions) && shouldRetry(error as ApiBaseQueryError),
})

export const baseQuery = (): BaseQueryFn<ApiRequestArgs, unknown, ApiBaseQueryError> =>
  baseQueryWithRetry
